import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  VerifyDeviceInput,
  UnassignDeviceResult,
  DeviceHistoryResult,
  DeviceListResult,
  DeviceListFilters,
} from "../../schemas/deviceSchema";
import { ReportFormat } from "../../schemas/reportSchema";

const VERIFY_DEVICE = gql`
  mutation VerifyDevice($input: VerifyDeviceInput!) {
    verifyDevice(input: $input)
  }
`;

const DEVICES = gql`
  query Devices(
    $page: Int
    $pageSize: Int
    $search: String
    $status: String
    $isVerified: Boolean
    $columnFilters: [DeviceColumnFilter!]
  ) {
    devices(
      page: $page
      pageSize: $pageSize
      search: $search
      status: $status
      isVerified: $isVerified
      columnFilters: $columnFilters
    ) {
      devices {
        id
        macAddress
        deviceUuid
        verificationCode
        status
        isVerified
        screenId
        screenName
        model
        os
        isPi
        lastSeenAt
        createdAt
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

const UNASSIGN_DEVICE = gql`
  mutation UnassignDevice($deviceId: ID!, $note: String) {
    unassignDevice(deviceId: $deviceId, note: $note) {
      success
      verificationCode
    }
  }
`;

const HISTORY_EVENT_FIELDS = `
  events {
    id
    deviceId
    deviceUuid
    event
    verificationCode
    screenId
    screenName
    actorUserId
    actorName
    source
    metadata
    createdAt
  }
  page
  pageSize
  total
  totalPages
`;

const DEVICE_HISTORY = gql`
  query DeviceHistory($deviceId: ID!, $page: Int, $pageSize: Int) {
    deviceHistory(deviceId: $deviceId, page: $page, pageSize: $pageSize) {
      ${HISTORY_EVENT_FIELDS}
    }
  }
`;

const DOWNLOAD_DEVICE_HISTORY = gql`
  query DownloadDeviceHistory($deviceId: ID!, $format: ReportFormat) {
    downloadDeviceHistory(deviceId: $deviceId, format: $format) {
      base64
      fileName
      mimeType
    }
  }
`;

const SCREEN_DEVICE_HISTORY = gql`
  query ScreenDeviceHistory($screenId: ID!, $page: Int, $pageSize: Int) {
    screenDeviceHistory(screenId: $screenId, page: $page, pageSize: $pageSize) {
      ${HISTORY_EVENT_FIELDS}
    }
  }
`;

export const deviceService = {
  getDevices: async (
    filters: DeviceListFilters = {},
  ): Promise<DeviceListResult> => {
    const { data } = await client.query({
      query: DEVICES,
      variables: {
        page: filters.page ?? 1,
        pageSize: filters.pageSize ?? 20,
        search: filters.search || null,
        status: filters.status || null,
        isVerified:
          typeof filters.isVerified === "boolean" ? filters.isVerified : null,
        columnFilters: filters.columnFilters?.length
          ? filters.columnFilters
          : null,
      },
      fetchPolicy: "network-only",
    });
    return data.devices;
  },

  verifyDevice: async (input: VerifyDeviceInput): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: VERIFY_DEVICE,
      variables: { input },
    });
    return data.verifyDevice;
  },

  unassignDevice: async (
    deviceId: string,
    note?: string,
  ): Promise<UnassignDeviceResult> => {
    const { data } = await client.mutate({
      mutation: UNASSIGN_DEVICE,
      variables: { deviceId, note: note || null },
    });
    return data.unassignDevice;
  },

  getDeviceHistory: async (
    deviceId: string,
    page = 1,
    pageSize = 50,
  ): Promise<DeviceHistoryResult> => {
    const { data } = await client.query({
      query: DEVICE_HISTORY,
      variables: { deviceId, page, pageSize },
      fetchPolicy: "network-only",
    });
    return data.deviceHistory;
  },

  // Exports the whole trail, not just the page the modal has loaded — the
  // server re-reads it unpaginated.
  downloadDeviceHistory: async (
    deviceId: string,
    format?: ReportFormat | null,
  ): Promise<{ base64: string; fileName: string; mimeType: string }> => {
    const { data } = await client.query({
      query: DOWNLOAD_DEVICE_HISTORY,
      variables: { deviceId, format: format || null },
      fetchPolicy: "network-only",
    });
    return data.downloadDeviceHistory;
  },

  getScreenDeviceHistory: async (
    screenId: string,
    page = 1,
    pageSize = 50,
  ): Promise<DeviceHistoryResult> => {
    const { data } = await client.query({
      query: SCREEN_DEVICE_HISTORY,
      variables: { screenId, page, pageSize },
      fetchPolicy: "network-only",
    });
    return data.screenDeviceHistory;
  },
};
